import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Button, CheckBox, Layer } from 'grommet';
import { Close, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { LastActivePeriodSelect } from '../forms/LastActivePeriodSelect';
import { ApiError, ClientService } from '/src/api';
import { Box, EntityIcon, FormCard, InfoBox, Input, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Client, ModelName } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { DateString, FormErrors, FormMetadata } from '/src/lib/types';
import { getFormErrors, getFormattedDateString, getModelLabel, getPeriodFromDateString } from '/src/utils';

export type RetireClientModalProps = {
  client: Client;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
  isCanada?: boolean;
};

export type TRetireClientModalForm = {
  last_active_reporting_quarter: DateString;
  end_date: DateString;
  retirement_reason: string;
};

export const RetireClientModal: React.FC<RetireClientModalProps> = observer(
  ({ client, setIsVisible, setShouldRefresh, isCanada = false }) => {
    /** Stores **/
    const userStore = useUserStore();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();

    /** State **/
    const [isRetiring, setIsRetiring] = useState(false);
    const [isConfirmed1, setIsConfirmed1] = useState(false);
    const [isConfirmed2, setIsConfirmed2] = useState(false);

    const [formValues, setFormValues] = useState<TRetireClientModalForm>({
      last_active_reporting_quarter: '',
      end_date: '',
      retirement_reason: '',
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    /** Computed **/
    const canRetire = userStore.isFuseAdmin;

    const selectedPeriodLabel = getPeriodFromDateString(formValues.last_active_reporting_quarter ?? '', isCanada);

    const hasReason = !!formValues.retirement_reason.trim();

    /** Methods **/
    const updateFormValue = (key: string, value: DateString) => {
      setFormValues((prev) => ({ ...prev, [key]: value }));
      if (formErrors[key]) {
        setFormErrors((prev) => ({ ...prev, [key]: '' }));
      }
    };

    const retireClient = async () => {
      if (isRetiring) return;

      const formFields: FormMetadata = {
        last_active_reporting_quarter: {
          label: t('client:lastReportingPeriod'),
          required: true,
        },
        end_date: {
          label: t('client:fuseEndDate'),
          required: true,
        },
        retirement_reason: {
          label: t('common:reasonForRetiring'),
          required: true,
        },
      };

      const errors = getFormErrors(formFields, {
        ...formValues,
        retirement_reason: formValues.retirement_reason.trim(),
      });
      if (errors) {
        setFormErrors(errors);
        return;
      }

      try {
        setIsRetiring(true);
        await ClientService.retire({
          id: client.id,
          last_active_reporting_quarter: formValues.last_active_reporting_quarter ?? '',
          end_date: formValues.end_date ?? '',
          retirement_reason: formValues.retirement_reason.trim(),
        });
        setShouldRefresh(true);
        setIsVisible(false);
        toast.success(toastMessages.retireClient.success);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.retireClient.error);
      } finally {
        setIsRetiring(false);
      }
    };

    /** Effects **/

    useEffect(() => {
      if (!canRetire) setIsVisible(false);
    }, [canRetire]);

    /** Render **/
    return (
      <Layer
        position="center"
        onEsc={() => setIsVisible(false)}
        onClickOutside={() => setIsVisible(false)}
        responsive={false}
        style={{
          width: 'auto',
          maxWidth: 'min(95vw, 500px)',
          maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: '8px',
        }}
      >
        <FormCard
          width="100%"
          title={`${t('client:retire')} ${getModelLabel(client, ModelName.Client)}`}
          icon={<EntityIcon entityName={ModelName.Client} />}
          onSubmit={() => retireClient()}
          isLoading={isRetiring}
          hideRequiredText
          saveButtonLabel={t('client:retireButton')}
          saveButtonLoadingLabel={t('client:retiringButton')}
          saveButtonDisabled={!isConfirmed1 || !isConfirmed2 || !hasReason}
          headerChildren={[
            <Button
              key="close"
              pad="none"
              onClick={() => setIsVisible(false)}
              icon={<Close size="20px" />}
              tip={t('common:close')}
            />,
          ]}
        >
          <Box pad={{ horizontal: 'medium', bottom: 'medium' }} gap="medium">
            <InfoBox gap="1rem" direction="row" align="center">
              <StatusWarning size="medium" color="status-warning" />
              <Text>
                {t('client:retireWarning')} <b>{t('common:after')}</b>{' '}
                {formValues.last_active_reporting_quarter ? selectedPeriodLabel : t('client:theLastReportingPeriod')}{' '}
                <b>{t('client:retireWarningEnd')}</b>
              </Text>
            </InfoBox>
            {client.client_programs.length > 0 ? (
              <LastActivePeriodSelect
                id="last_active_reporting_quarter"
                label={t('client:lastReportingPeriod')}
                placeholder={t('client:selectPeriod')}
                value={formValues.last_active_reporting_quarter}
                setValue={(value) => updateFormValue('last_active_reporting_quarter', value)}
                clientId={client.id}
                fullClientLoad
                reportingPeriodTypeId={client.reporting_period_type_id}
                firstActivePeriod={client.first_active_reporting_quarter}
                error={formErrors['last_active_reporting_quarter']}
                required
                fill="horizontal"
              />
            ) : (
              <InfoBox gap="1rem" direction="row" align="center">
                <StatusWarning size="medium" color="status-warning" />
                <Text>{t('client:noProgramMessage')}</Text>
              </InfoBox>
            )}
            <Input
              name="end_date"
              label={t('client:fuseEndDate')}
              fill="horizontal"
              value={formValues.end_date}
              setValue={(value) => updateFormValue('end_date', value)}
              error={formErrors['end_date']}
              componentType="date"
              dateBounds={
                formValues.last_active_reporting_quarter
                  ? [
                      dayjs(formValues.last_active_reporting_quarter).format('YYYY-MM-DD') as DateString,
                      dayjs().add(50, 'year').format('YYYY-MM-DD') as DateString,
                    ]
                  : undefined
              }
              displayValue={getFormattedDateString(formValues.end_date ?? '', isCanada)}
              required
            />
            <Input
              name="retirement_reason"
              label={t('common:reasonForRetiring')}
              fill="horizontal"
              value={formValues.retirement_reason}
              setValue={(value) => updateFormValue('retirement_reason', value)}
              error={formErrors['retirement_reason']}
              componentType="textArea"
              rows={3}
              required
            />
            <Box gap="small">
              {formValues.last_active_reporting_quarter && formValues.end_date && hasReason && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed1} onChange={() => setIsConfirmed1(!isConfirmed1)} color="white" />
                  <Text color="white">{t('client:retireConfirmation1')}</Text>
                </InfoBox>
              )}
              {formValues.last_active_reporting_quarter && formValues.end_date && hasReason && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed2} onChange={() => setIsConfirmed2(!isConfirmed2)} color="white" />
                  <Text color="white">
                    {t('client:retireConfirmation2Start')} <b>{t('common:after')}</b> {selectedPeriodLabel}{' '}
                    {t('client:retireConfirmation2End')}
                  </Text>
                </InfoBox>
              )}
            </Box>
          </Box>
        </FormCard>
      </Layer>
    );
  }
);
